import { zPlatformStats, type PlatformStats } from '@racksavant/core';
import { useEffect, useState } from 'react';

/* =========================================================================
   Live public counts.

   The marketing site is allowed to state facts about the product. It is not
   allowed to invent them. So this hook has exactly one failure mode — `null`
   — and every caller must read correctly with no number at all:

     "68 garments on the rack"  →  "Garments on the rack"

   There is no placeholder, no last-known value, no "1,000+". A count that is
   not measured is not printed. Zero is treated the same way as an error,
   because "0 looks on the Runway" is a fact nobody needs on a landing page.
   ========================================================================= */

const API_URL = (import.meta.env.VITE_API_URL ?? 'http://localhost:8787').replace(/\/+$/, '');
const STATS_URL = `${API_URL}/v1/stats`;

/** Network data, so it is validated rather than trusted. */
function parseStats(value: unknown): PlatformStats | null {
  const parsed = zPlatformStats.safeParse(value);
  return parsed.success ? parsed.data : null;
}

export function useStats(): PlatformStats | null {
  const [stats, setStats] = useState<PlatformStats | null>(null);

  useEffect(() => {
    let live = true;
    const controller = typeof AbortController === 'undefined' ? null : new AbortController();

    fetch(STATS_URL, { signal: controller?.signal })
      .then((response) => (response.ok ? response.json() : null))
      .then((json) => {
        if (live) setStats(parseStats(json));
      })
      .catch(() => {
        /* No counts is a supported state — callers render the copy without one. */
      });

    return () => {
      live = false;
      controller?.abort();
    };
  }, []);

  return stats;
}
